import React, { useContext, useState, useEffect } from "react";
import {
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import LoginIcon from "@mui/icons-material/Login";
import CloseIcon from "@mui/icons-material/Close";
import { FormRowStyle, FormInputStyle } from "../utils/AAStyles";
import { useTheme } from "@mui/material/styles";
import { UserContext } from "../context/userContext";
import { LoginUser, WhoAmI } from "../configuration/AAConfig_Security";

interface LoginDialogV1Props {
  title: string;
  message: string;
}

const LoginDialogV1: React.FC<LoginDialogV1Props> = ({ title, message }) => {
  const theme = useTheme();

  const {
    showLogin,
    updateShowLogin,
    updateLoginDetails,
    updateCurrentUser,
    updateUserError,
    userError,
    updateUseAuthenticate,
  } = useContext(UserContext);

  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<string | null>(userError);

  const handleUsernameChangeEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value);
  };

  const handlePasswordChangeEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
  };

  const handleShowPasswordClick = () => {
    setShowPassword(!showPassword);
  };

  const IsLoginValid = (): boolean => {
    const bUserNameEmpty = !username || username.length === 0;
    const bPasswordEmpty = !password || password.length === 0;

    return !(bUserNameEmpty || bPasswordEmpty);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleLoginClick = async () => {
    if (!IsLoginValid()) {
      updateUserError("One or more credentials is empty.");
      return;
    }

    const loginRes = await LoginUser(username, password, setLoginError);
    updateLoginDetails(loginRes);

    console.log("login result", loginRes);
    if (loginRes) {
      const userInfo = await WhoAmI(loginRes.token);

      if (userInfo) {
        console.log("UserInfo", userInfo);
        if (
          userInfo.rights.findIndex(
            (x: string) => x === "SuperAdministrator" || x === "OrganisationAdministrator"
          ) < 0
        ) {
          setLoginError("You don't have sufficient rights to edit other users.");
        } else {
          if (userInfo.active && !userInfo.isDeleted) {
            updateLoginDetails(loginRes);
            updateCurrentUser(userInfo);
            updateShowLogin(false);
          } else {
            updateCurrentUser(null);
            setLoginError(
              !userInfo.active
                ? "You are not an active user on this system."
                : "This user has been deleted."
            );
          }
        }
      } else {
        setLoginError("Unable to get user information.");
      }
    } else {
      updateUserError("Unable to login with the supplied credentials.");
    }
  };

  const handleCancelClick = () => {
    updateLoginDetails(null);
    updateShowLogin(false);
  };

  useEffect(() => {
    updateUseAuthenticate(false);
  }, [updateUseAuthenticate]);

  useEffect(() => {
    setLoginError(userError);
  }, [userError]);

  useEffect(() => {
    if (loginError && loginError.length > 0) {
      if (password.length > 0) setPassword("");
      updateShowLogin(true);
    }
  }, [loginError, password.length, updateShowLogin]);

  return (
    <Dialog
      open={showLogin}
      aria-labelledby="confirm-edit-loss-dialog"
      fullWidth
      maxWidth="xs"
      disableEnforceFocus
    >
      <DialogTitle
        id="confirm-edit-loss-dialog"
        sx={{ color: "#FFFFFF", backgroundColor: "#2A6EBB" }}
      >
        {title || "Login"} (v1)
      </DialogTitle>
      <DialogContent sx={{ marginTop: theme.spacing(1) }}>
        {loginError ? (
          <Typography variant="body1" color="error">
            {loginError}
          </Typography>
        ) : (
          <Typography variant="body1">{message}</Typography>
        )}
        <Grid container justifyContent="flex-start" alignItems="center" sx={FormRowStyle()}>
          <Grid item xs={3}>
            <Typography
              id="aa-text-label-username"
              variant="body2"
              color="initial"
              align="left"
            >
              Username*
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <TextField
              id="aa-text-textfield-username"
              sx={FormInputStyle()}
              fullWidth
              required
              variant="outlined"
              margin="dense"
              size="small"
              value={username}
              onChange={handleUsernameChangeEvent}
              aria-labelledby="aa-text-label-username"
            />
          </Grid>
          <Grid item xs={3}>
            <Typography
              id="aa-text-label-password"
              variant="body2"
              color="initial"
              align="left"
            >
              Password*
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <TextField
              id="aa-text-textfield-password"
              sx={FormInputStyle()}
              type={showPassword ? "text" : "password"}
              fullWidth
              required
              variant="outlined"
              margin="dense"
              size="small"
              value={password}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleShowPasswordClick}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      sx={{
                        "&:hover": {
                          color: "#2A6EBB",
                        },
                      }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={handlePasswordChangeEvent}
              aria-labelledby="aa-text-label-password"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ marginBottom: theme.spacing(1) }}>
        <Button
          onClick={handleLoginClick}
          autoFocus
          variant="contained"
          sx={{
            color: "#FFFFFF",
            backgroundColor: "#2A6EBB",
            "&:hover": {
              backgroundColor: "#00AEEF",
              color: "#FFFFFF",
            },
          }}
          startIcon={<LoginIcon />}
        >
          Login
        </Button>
        <Button
          onClick={handleCancelClick}
          sx={{
            color: "#2A6EBB",
            "&:hover": {
              backgroundColor: "#00AEEF",
              color: "#FFFFFF",
            },
          }}
          startIcon={<CloseIcon />}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoginDialogV1;
